import {createFeatureSelector, createSelector, MemoizedSelector} from '@ngrx/store';
import {SeFePaginationOptions} from 'se-fe-pagination';
import {CatalogSummary, CollectionMetadata} from '../../../models/clare';
import {ProductManagementErrorInterface} from '../../../error/error-handling/product-management-error';
import {DEFAULT_PRODUCT_PAGINATION_OPTIONS} from '../../../utils/pagination-utils';
import {
  CATALOG_STATE_KEY,
  CatalogState,
  CatalogRegistrationsRequestState,
  CreateCatalogStatus,
  DeleteCatalogRequestState,
  getCatalogs,
  getCatalogRegistrations,
  getCreatedCatalogId,
  getCreateStatus,
  getDeletedCatalogId,
  getDeleteStatus,
  getError,
  getLoaded,
  getLoading,
  getPaginationMeta,
} from './catalog.reducer';

export const selectCatalogState: MemoizedSelector<object, CatalogState> =
  createFeatureSelector<CatalogState>(CATALOG_STATE_KEY);

export const selectCatalogs: MemoizedSelector<object, CatalogSummary[]> = createSelector(
  selectCatalogState,
  getCatalogs
);

export const selectCatalogPaginationMeta: MemoizedSelector<object, CollectionMetadata> = createSelector(
  selectCatalogState,
  getPaginationMeta
);

export const selectCatalogPaginationOptions: MemoizedSelector<object, SeFePaginationOptions> = createSelector(
  selectCatalogPaginationMeta,
  ({pagination}): SeFePaginationOptions => ({
    name: 'Catalogs pagination options',
    label: 'Catalogs',
    currentPage: pagination?.current_page ?? 1,
    total: pagination?.total ?? 0,
    limit: pagination?.limit ?? DEFAULT_PRODUCT_PAGINATION_OPTIONS.limit,
    limits: DEFAULT_PRODUCT_PAGINATION_OPTIONS.limits,
  })
);

export const selectCatalogsLoading: MemoizedSelector<object, boolean> = createSelector(selectCatalogState, getLoading);

export const selectCatalogsLoaded: MemoizedSelector<object, boolean> = createSelector(selectCatalogState, getLoaded);

export interface CreateCatalogRequestState {
  status: CreateCatalogStatus;
  catalogId: number | undefined;
}

export const selectCreateCatalogRequestState: MemoizedSelector<object, CreateCatalogRequestState> = createSelector(
  selectCatalogState,
  state => ({
    status: getCreateStatus(state),
    catalogId: getCreatedCatalogId(state),
  })
);

export const selectDeleteCatalogRequestState: MemoizedSelector<object, DeleteCatalogRequestState> = createSelector(
  selectCatalogState,
  state => ({
    status: getDeleteStatus(state),
    catalogId: getDeletedCatalogId(state),
  })
);

export const selectCatalogRegistrationsRequestState: MemoizedSelector<object, CatalogRegistrationsRequestState> =
  createSelector(selectCatalogState, getCatalogRegistrations);

export const selectCatalogError: MemoizedSelector<object, ProductManagementErrorInterface | undefined> = createSelector(
  selectCatalogState,
  getError
);
